export type UsageEventName =
  | 'page_view'
  | 'page_engagement'
  | 'menu_click'
  | 'feature_action'
  | 'query_submitted'
  | 'query_completed'
  | 'session_started'
  | 'session_ended';

export interface UsageEventInput {
  event_name: UsageEventName;
  occurred_at?: string;
  session_id?: string;
  page_view_id?: string;
  interaction_id?: string;
  page_key?: string;
  feature_key?: string;
  action?: string;
  outcome?: string;
  route?: string;
  referrer_route?: string;
  brand_id?: string;
  site_id?: string;
  channel?: string;
  duration_ms?: number;
  properties?: Record<string, unknown>;
}

const sessionStorageKey = 'minglu_usage_session_id';
const sessionStartedStorageKey = 'minglu_usage_session_started';
const sessionEndedStorageKey = 'minglu_usage_session_ended';

type PendingMenuNavigation = {
  route: string;
  label: string;
  sourceRoute: string;
  createdAt: number;
};

let pendingMenuNavigation: PendingMenuNavigation | undefined;

function createID(prefix: string) {
  try {
    return crypto.randomUUID();
  } catch {
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

export function getUsageSessionID() {
  let value = sessionStorage.getItem(sessionStorageKey);
  if (!value) {
    value = createID('session');
    sessionStorage.setItem(sessionStorageKey, value);
  }
  return value;
}

/** 同一浏览器标签页只写一次会话开始；会话结束使用 pagehide 的 keepalive 请求。 */
export function trackUsageSessionStart(input: Omit<UsageEventInput, 'event_name' | 'session_id'> = {}) {
  const sessionID = getUsageSessionID();
  if (sessionStorage.getItem(sessionStartedStorageKey) === sessionID) return;
  sessionStorage.setItem(sessionStartedStorageKey, sessionID);
  trackUsageEvent({
    event_name: 'session_started',
    session_id: sessionID,
    feature_key: 'session_tracking',
    action: 'start',
    route: input.route || window.location.pathname,
    ...input,
  });
}

export function trackUsageSessionEnd(input: Omit<UsageEventInput, 'event_name' | 'session_id'> = {}) {
  const sessionID = getUsageSessionID();
  if (sessionStorage.getItem(sessionEndedStorageKey) === sessionID) return;
  sessionStorage.setItem(sessionEndedStorageKey, sessionID);
  trackUsageEvent({
    event_name: 'session_ended',
    session_id: sessionID,
    feature_key: 'session_tracking',
    action: 'end',
    route: input.route || window.location.pathname,
    ...input,
  }, true);
}

export function pageKeyFromRoute(route: string) {
  const value = route.replace(/^\/+|\/+$/g, '');
  return value ? value.replace(/\//g, '_').replace(/[^a-zA-Z0-9_-]/g, '') : 'home';
}

/** 直发单条产品事件；网络失败不影响任何业务交互。 */
export function trackUsageEvent(input: UsageEventInput, keepalive = false): Promise<void> {
  const token = localStorage.getItem('access_token');
  if (!token) return Promise.resolve();
  const body = JSON.stringify({
    client_event_id: createID('event'),
    occurred_at: input.occurred_at || new Date().toISOString(),
    session_id: input.session_id || getUsageSessionID(),
    properties: input.properties || {},
    ...input,
  });
  return fetch('/api/v1/usage-events', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body,
    keepalive,
  }).then(async (response) => {
    if (!response.ok) console.warn('[usage-tracking] event was rejected', response.status, await response.text(), input.event_name, input.feature_key, input.action);
  }).catch((error) => console.warn('[usage-tracking] event request failed', error));
}

export function trackMenuClick(route: string, label: string, sourceRoute: string) {
  // 菜单点击与到达页面合并成一条 page_view，避免同一次导航出现多条日志。
  pendingMenuNavigation = { route, label, sourceRoute, createdAt: Date.now() };
}

export function consumeMenuNavigation(route: string) {
  const pending = pendingMenuNavigation;
  pendingMenuNavigation = undefined;
  if (!pending || pending.route !== route || Date.now() - pending.createdAt > 5000) return undefined;
  return pending;
}

/**
 * 业务页面在用户确实点击查询、导出、保存、发布等操作后调用。
 * 不自动监听 HTTP 请求，避免一个页面的多个 POST 被误记成多次业务操作。
 */
export function trackFeatureAction(input: Omit<UsageEventInput, 'event_name' | 'route'> & { route?: string }): Promise<void> {
  return trackUsageEvent({
    event_name: 'feature_action',
    route: input.route || window.location.pathname,
    outcome: input.outcome || 'success',
    ...input,
  });
}

/** 查询类功能使用同一事件口径，提交与完成分别记录，便于统计成功率与耗时。 */
export function trackQueryEvent(
  stage: 'submitted' | 'completed',
  input: Omit<UsageEventInput, 'event_name' | 'route'> & { route?: string },
) {
  trackUsageEvent({
    event_name: stage === 'submitted' ? 'query_submitted' : 'query_completed',
    route: input.route || window.location.pathname,
    ...input,
  });
}
